import { Link } from "react-router-dom";
import { KpiTile } from "@/components/kpi-tile";
import { MonthlyTrendsCard } from "@/components/monthly-trends-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/api/queries/me";
import { useDepartments, useDepartmentOverview } from "@/api/queries/departments";
import { useAppStore } from "@/store/app-store";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";
import { formatDateTime, formatInteger } from "@/lib/format";

export function OverviewPage() {
  const { identity } = useAuth();
  const me = useCurrentUser();
  const isPlatformAdmin = isRespaiAdmin(identity?.role);
  const selectedDeptId = useAppStore((s) => s.selectedDepartmentId);
  const { data: departments } = useDepartments();
  const selectedDept = departments?.find((d) => d.id === selectedDeptId);
  const deptId = selectedDeptId ?? me.data?.department?.id;
  const tz = selectedDept?.timezone ?? me.data?.department?.timezone;
  const deptName = isPlatformAdmin
    ? selectedDept?.name ??
      (selectedDeptId === "all" ? "All departments" : "Select a department")
    : me.data?.department?.name ?? "Loading department…";
  const overview = useDepartmentOverview(deptId);

  const kpiSkeletons = (
    <>
      <Skeleton className="h-28" />
      <Skeleton className="h-28" />
      <Skeleton className="h-28" />
      <Skeleton className="h-28" />
    </>
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Department overview</h1>
        <p className="text-sm text-muted-foreground">{deptName}</p>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {!deptId ? (
          isPlatformAdmin ? (
            <p className="col-span-full text-sm text-muted-foreground">
              Select a department to view KPIs.
            </p>
          ) : (
            kpiSkeletons
          )
        ) : overview.isLoading ? (
          kpiSkeletons
        ) : overview.isError ? (
          <p className="col-span-full text-sm text-destructive">
            Failed to load KPIs.
          </p>
        ) : overview.data ? (
          <>
            <KpiTile
              label="Incidents this month"
              value={formatInteger(overview.data.kpis.incidents_this_month)}
              to="/incidents"
            />
            {/* Clicking the trend opens the interpretive "explained" page —
                what the carcinogen metric meant at the time and what it means
                today (CO + VOC vs NIOSH REL). */}
            <MonthlyTrendsCard
              data={overview.data.monthly_trends}
              to="/exposure-insights"
            />
            {/* Drill down to the list of every incident whose readings add up to
                this number, each openable (see ExposureIncidentsPage). */}
            <KpiTile
              label="Peak gas exposures"
              value={formatInteger(overview.data.kpis.peak_gas_exposures)}
              hint="Peak readings over a NIOSH limit (screen)"
              to="/exposure-incidents"
            />
            {/* "open" maps to status === "active" in the incidents query. */}
            <KpiTile
              label="Active incidents"
              value={formatInteger(overview.data.kpis.open_incidents)}
              to="/incidents?status=open"
            />
          </>
        ) : null}
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Recent activity</CardTitle>
        </CardHeader>
        <CardContent>
          {!deptId ? (
            isPlatformAdmin ? (
              <p className="text-sm text-muted-foreground">
                Select a department to view recent activity.
              </p>
            ) : (
              <Skeleton className="h-32" />
            )
          ) : overview.isLoading ? (
            <Skeleton className="h-32" />
          ) : overview.isError ? (
            <p className="text-sm text-destructive">
              Failed to load recent activity.
            </p>
          ) : overview.data && overview.data.recent_activity.length > 0 ? (
            <ul className="divide-y divide-border">
              {overview.data.recent_activity.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between py-2 text-sm"
                >
                  <div>
                    {item.link ? (
                      <Link
                        className="text-primary hover:underline"
                        to={item.link}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <span>{item.label}</span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(item.ts, tz)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No recent activity.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
